<template>
  <Transition name="live-banner">
    <section v-if="isShown" ref="bannerEl" class="gam-live-banner" :aria-label="localize('live.banner.label')">
      <div class="rows">
        <div v-for="item in shownItems" :key="item.id" :class="['row', `is-${item.kind}`]">
          <!-- A gambit or spot: the whole row opens it, the button is the next step. -->
          <template v-if="item.kind !== 'handshake'">
            <button type="button" class="body" @click="openItem(item)">
              <span :class="['mark', { 'is-live': item.kind === 'checked-in' || item.kind === 'spot' }]">
                <!-- Ended uses the same clock the overlay shows for how long ago, in the pin's grey. -->
                <GamIcon
                  :name="item.kind === 'recent' ? GamIconName.LAST_TIME : GamIconName.PIN"
                  :size="GamIconSize.SMALL"
                  is-current
                />
              </span>
              <span class="text">
                <span class="title-line">
                  <GamIcon
                    v-if="item.kind !== 'spot'"
                    :class="['gambit-mark', { 'is-past': item.kind === 'recent' }]"
                    :name="GamIconName.PLAY_EMPTY"
                    size="12px"
                    is-current
                  />
                  <span :class="['title', { 'is-past': item.kind === 'recent' }]">{{ itemTitle(item) }}</span>
                </span>
                <span class="meta">{{ itemMeta(item) }}</span>
              </span>
            </button>

            <GamButton
              v-if="item.kind === 'check-in'"
              :variant="GamButtonVariant.HYPE"
              :size="GamButtonSize.SMALL"
              :left-icon="GamIconName.PLAY_EMPTY"
              :is-loading="busyId === item.id"
              :is-disabled="!!busyId"
              label="live.banner.checkIn"
              @click="checkIn(item.gambit, item.id)"
            />
            <GamButton
              v-else-if="item.kind === 'checked-in' || item.kind === 'spot'"
              :variant="GamButtonVariant.SECONDARY"
              :size="GamButtonSize.SMALL"
              :is-loading="busyId === item.id"
              :is-disabled="!!busyId"
              label="live.banner.checkOut"
              @click="checkOut(item)"
            />
            <button
              v-else-if="item.kind === 'recent'"
              type="button"
              class="close"
              :aria-label="localizeWithValues('live.banner.dismissRecent', [item.gambit.title])"
              @click="dismissRecent(item.gambit.id)"
            >
              <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.SMALL" is-current />
            </button>
          </template>

          <!-- A live handshake: the picture and name open the player, the rest of the row plays. -->
          <template v-else>
            <div class="body">
              <component
                :is="item.handshake.peer ? RouterLink : 'span'"
                v-bind="item.handshake.peer ? { to: `/@${item.handshake.peer.username}?back=history` } : {}"
                class="peer"
              >
                <img
                  v-if="item.handshake.peer?.profileImageBlobHash"
                  class="picture"
                  :src="imageSrc(item.handshake.peer.profileImageBlobHash)"
                />
                <span v-else class="picture missing">
                  <GamIcon :name="GamIconName.PAWN_EMPTY" :size="GamIconSize.SMALL" />
                </span>
              </component>
              <div class="text play-area" @click="openGame(item.handshake.id)">
                <component
                  :is="item.handshake.peer ? RouterLink : 'span'"
                  v-bind="item.handshake.peer ? { to: `/@${item.handshake.peer.username}?back=history` } : {}"
                  class="title username"
                  @click.stop
                >
                  {{ peerName(item.handshake) }}
                </component>
                <span class="meta">
                  <GamIcon :name="GamIconName.PIN" :size="GamIconSize.SMALL" is-primary />
                  <span>{{ timeLeft(item.handshake.liveUntil) }}</span>
                </span>
              </div>
            </div>

            <HandshakeButton
              class="handshake-cta"
              :aria-label="localize('handshake.game.openSuggestion')"
              :responder-id="item.handshake.responderId"
              :is-completed="false"
              :is-pending="false"
              is-recommendation
              :is-unplayed="!hasPlayedPosition(item.handshake)"
              :position="currentRecommendation(item.handshake).position"
              :position-on="isAsking(item.handshake)"
              @click="openGame(item.handshake.id)"
            />

            <!-- Play started this position and nobody has answered: the disc still opens the game, this answers. -->
            <button v-if="isAsking(item.handshake)" type="button" class="played-cta" @click="askPlayed(item.handshake)">
              {{ localize('handshake.played.short') }}
            </button>

            <button
              type="button"
              class="close"
              :aria-label="localizeWithValues('handshake.close.label', [peerName(item.handshake)])"
              @click="closeHandshake(item.handshake)"
            >
              <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.SMALL" is-current />
            </button>
          </template>
        </div>
      </div>

      <button v-if="items.length > 1" type="button" class="more" @click="isExpanded = !isExpanded">
        {{
          isExpanded ? localize('live.banner.less') : localizeWithValues('live.banner.more', [String(items.length - 1)])
        }}
        <GamIcon
          :name="isExpanded ? GamIconName.ARROW_UP : GamIconName.ARROW_DOWN"
          :size="GamIconSize.SMALL"
          is-current
        />
      </button>
    </section>
  </Transition>

  <HandshakePlayedDialog
    v-if="playedQuestion"
    :position="playedQuestion.position"
    :coplayer="peerName(playedQuestion.handshake)"
    :busy="isAnswering"
    @answer="onPlayedAnswer"
  />
</template>

<script setup lang="ts">
import { RouterAdminType, RouterAuthType, RouterNameType, StaticPageName } from '@/core/data/config/uiConfig.interface';
import type { LiveGambitDto } from '@/core/data/gambit/gambit.interface';
import type { LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import { getTimeRemaining } from '@/core/helpers/date.helper';
import { AppEndpoint } from '@/core/network/api/constants/api.url.constant';
import { useAuthStore } from '@/stores/auth.store';
import { useLiveBannerStore } from '@/stores/liveBanner.store';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import type { LiveBannerItem } from '@/views/components/gam-live-banner/live-banner-items';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { refreshSoloClockDraw } from '@/views/main/clock/utils/clock-handshake';
import { openLiveHandshakeClock } from '@/views/main/clock/utils/open-handshake-clock';
import HandshakeButton from '@/views/main/handshakes/HandshakeButton.vue';
import HandshakePlayedDialog from '@/views/main/handshakes/HandshakePlayedDialog.vue';
import { assignPlayedPhotos } from '@/views/main/handshakes/utils/assign-played-photos';
import { hasPlayedPosition, passerbyDisplayName } from '@/views/main/handshakes/utils/handshake-game';
import { clearPlayedPrompt, readPlayedPrompt } from '@/views/main/handshakes/utils/played-prompt';
import { currentRecommendation } from '@/views/main/handshakes/utils/recommendation-state';
import { useElementSize, useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

/**
 * Screens that are a game in themselves, the full-screen sparks deck, and the
 * screens the banner rows open: the gambit and the spot already show
 * everything a row would say.
 */
const HIDDEN_ROUTES = new Set<string>([
  RouterNameType.SPARKS,
  RouterNameType.CLOCK,
  RouterNameType.WALK,
  RouterNameType.WALKS,
  RouterNameType.DUEL,
  RouterNameType.GAMBIT,
  ...Object.values(RouterAuthType),
  ...Object.values(RouterAdminType),
  ...Object.values(StaticPageName),
]);

const route = useRoute();
const router = useRouter();
const toast = useToast();
const now = useNow({ interval: 30_000 });

const { user } = storeToRefs(useAuthStore());
const store = useLiveBannerStore();
const handshakesStore = useLiveHandshakesStore();
const { items } = storeToRefs(store);
const isExpanded = ref(true);
/** The row checking in or out. */
const busyId = ref<string | null>(null);

if (user.value?.id) store.start();
watch(
  () => route.fullPath,
  () => store.loadIfStale(),
);

const isHiddenRoute = computed(() => HIDDEN_ROUTES.has(String(route.name ?? '')) || !!route.query.spot);

const isShown = computed(() => !!user.value?.id && !isHiddenRoute.value && items.value.length > 0);

const shownItems = computed(() => (isExpanded.value ? items.value : items.value.slice(0, 1)));

// The app is laid out under the banner rather than behind it.
const bannerEl = ref<HTMLElement>();
const { height: bannerHeight } = useElementSize(bannerEl, undefined, { box: 'border-box' });
watch(
  () => (isShown.value ? bannerHeight.value : 0),
  (height) => document.documentElement.style.setProperty('--live-banner-height', `${height}px`),
  { immediate: true },
);
onBeforeUnmount(() => document.documentElement.style.removeProperty('--live-banner-height'));

const imageSrc = (blobHash: string) => {
  const url = `${gambits.configService.getServiceRoutingHost()}${AppEndpoint.BLOB}`.replace(/{blobHash}/g, blobHash);
  return `${url}?width=100`;
};

const peerName = (handshake: LiveHandshakeDto): string =>
  handshake.peer
    ? `@${handshake.peer.username}`
    : passerbyDisplayName(localize('handshake.player.passerby'), handshake.passerbyClaimCode, true);

// `now` makes the labels follow the ticker.
const timeLeft = (until: string) =>
  now.value ? localizeWithValues('handshake.open.timeLeft', [getTimeRemaining(until)]) : '';

const itemTitle = (item: LiveBannerItem): string => {
  if (item.kind === 'spot') return item.checkIn.locationName;
  if (item.kind === 'handshake') return peerName(item.handshake);
  return item.gambit.title;
};

const itemMeta = (item: LiveBannerItem): string => {
  if (item.kind === 'spot') return localize('live.banner.checkedIn');
  if (item.kind === 'recent') return localize('live.banner.recent');
  if (item.kind === 'handshake') return timeLeft(item.handshake.liveUntil);

  if (item.kind === 'checked-in') {
    return localizeWithValues('live.banner.checkedInTimeLeft', [getTimeRemaining(item.gambit.end)]);
  }

  return now.value < new Date(item.gambit.start)
    ? localizeWithValues('live.banner.startsIn', [getTimeRemaining(item.gambit.start)])
    : timeLeft(item.gambit.end);
};
const openItem = (item: LiveBannerItem) => {
  if (item.kind === 'spot') {
    router.push({
      name: RouterNameType.LOCATIONS,
      query: {
        spot: item.checkIn.locationId,
        lat: String(item.checkIn.coordinates.lat),
        lng: String(item.checkIn.coordinates.lng),
        zoom: '15',
      },
    });
    return;
  }

  if (item.kind === 'handshake') return;

  router.push({ name: RouterNameType.GAMBIT, params: { id: item.gambit.id }, query: { back: 'history' } });
};

const openGame = (handshakeId: string) =>
  void openLiveHandshakeClock(handshakeId, localize('handshake.player.passerby'));

const checkIn = async (gambit: LiveGambitDto, itemId: string) => {
  if (busyId.value) return;

  busyId.value = itemId;
  try {
    if (await store.checkIn(gambit)) {
      router.push({ name: RouterNameType.GAMBIT, params: { id: gambit.id }, query: { back: 'history' } });
    } else {
      toast.error(localize('live.banner.checkInError'));
    }
  } finally {
    busyId.value = null;
  }
};

const checkOut = async (item: LiveBannerItem) => {
  if (busyId.value) return;

  const target =
    item.kind === 'spot'
      ? { locationId: item.checkIn.locationId, gambitId: item.checkIn.gambitId }
      : item.kind === 'checked-in' && item.gambit.location
        ? { locationId: item.gambit.location.id, gambitId: item.gambit.id }
        : null;
  if (!target) return;

  busyId.value = item.id;
  try {
    await store.checkOut(target.locationId, target.gambitId);
  } catch {
    toast.error(localize('live.banner.checkOutError'));
  } finally {
    busyId.value = null;
  }
};

const dismissRecent = async (gambitId: string) => {
  const result = await store.dismissRecent(gambitId);
  if (result.error) toast.error(localize('live.banner.dismissError'));
};

const closeNow = async (handshake: LiveHandshakeDto) => {
  const result = await store.closeHandshake(handshake);
  if (result.error) toast.error(result.error.message || localize('handshake.close.error'));
};

/** A position Play started and nobody answered. Closing asks about it first, the way the clock's X does. */
const openPositionOf = (handshake: LiveHandshakeDto): number | null => {
  const recommendation = currentRecommendation(handshake);
  if (recommendation.state === 'open') return recommendation.position;

  const waiting = readPlayedPrompt();
  if (waiting?.handshakeId === handshake.id) return waiting.position;

  return handshake.openPositions[0] ?? null;
};

/**
 * Whether the row asks Played. Only the server's open positions count, so once
 * either player answers, the other's row stops asking.
 */
const isAsking = (handshake: LiveHandshakeDto): boolean => currentRecommendation(handshake).state === 'open';

const playedQuestion = ref<{ handshake: LiveHandshakeDto; position: number; thenClose: boolean } | null>(null);
const isAnswering = ref(false);

const askPlayed = (handshake: LiveHandshakeDto) => {
  const { position } = currentRecommendation(handshake);
  if (position != null) playedQuestion.value = { handshake, position, thenClose: false };
};

/** The X closes at once. A position still waiting for Played is asked first, and closes after the answer. */
const closeHandshake = async (handshake: LiveHandshakeDto) => {
  const position = openPositionOf(handshake);
  if (position != null) {
    playedQuestion.value = { handshake, position, thenClose: true };
    return;
  }

  await closeNow(handshake);
};

/** Yes saves the position, No settles it for both phones. From the X, the handshake then closes. */
const onPlayedAnswer = async (yes: boolean) => {
  const question = playedQuestion.value;
  if (!question || isAnswering.value) return;

  const { handshake, position, thenClose } = question;
  isAnswering.value = true;
  const result = yes
    ? await gambits.userService.checkHandshakePosition(handshake.id, position)
    : await gambits.userService.declineHandshakePosition(handshake.id, position);
  if (result.error || !result.data) {
    isAnswering.value = false;
    toast.error(result.error?.message || localize('handshake.positions.failed'));
    return;
  }

  const waiting = readPlayedPrompt();
  if (waiting?.handshakeId === handshake.id && waiting.position === position) {
    if (yes) {
      const error = await assignPlayedPhotos(waiting);
      if (error) toast.error(error);
    }
    clearPlayedPrompt();
  }

  handshakesStore.patch(handshake.id, {
    openPositions: handshake.openPositions.filter((open) => open !== position),
    ...('positions' in result.data ? { positions: result.data.positions } : {}),
  });
  // Answered off the clock, so the clock never got to ask again for its own number.
  if (yes) void refreshSoloClockDraw((seed, round) => gambits.userService.dealHandshakePosition(seed, round));

  isAnswering.value = false;
  playedQuestion.value = null;
  if (thenClose) {
    await closeNow(handshake);
  } else {
    void handshakesStore.load();
  }
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.gam-live-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Under the gambit and spot overlays, which cover the screen and say the same things. */
  z-index: 450;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--spacing-xs);
  padding: calc(var(--sai-top) + var(--spacing-s)) var(--spacing-md) var(--spacing-s);
  background: var(--color-dark-700);
  border-bottom: 1px solid var(--color-white-10);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: 100%;
  max-width: 560px;
  max-height: 50svh;
  /* Room inside the scroll box for the raised buttons' shadow, which it would
     otherwise cut on the last row. */
  margin: 0 auto calc(-1 * var(--spacing-md));
  padding-bottom: var(--spacing-md);
  /* Vertical scroll only. overflow-y: auto would otherwise allow a sideways
     bar from the close button's hit area, which sticks out past the row. */
  overflow-x: hidden;
  overflow-y: auto;
}

.row {
  display: flex;
  align-items: center;
  gap: var(--spacing-s);
  min-height: 48px;
}

.body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-white-100);
  text-align: left;
  cursor: pointer;
}

.mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-white-5);
  color: var(--color-white-70);

  &.is-live {
    background: var(--color-pink-20);
    color: var(--color-pink);
  }
}

.peer {
  display: flex;
  flex-shrink: 0;
}

.picture {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  filter: saturate(0);

  &.missing {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-white-10);
  }
}

.text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.play-area {
  flex: 1;
  align-self: stretch;
  justify-content: center;
  cursor: pointer;
}

.username {
  align-self: flex-start;
  max-width: 100%;
}

.title,
.meta > span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title-line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.gambit-mark {
  flex-shrink: 0;
  color: var(--color-pink);

  &.is-past {
    color: var(--color-white-50);
  }
}

.title {
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-white-100);

  /* An ended gambit reads in the clock's grey. */
  &.is-past {
    color: var(--color-white-70);
  }
}

a.title {
  text-decoration: none;

  @include hover {
    color: var(--color-pink);
  }
}

.meta {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: var(--color-white-50);
}

.handshake-cta {
  height: 40px;

  &:not(.has-cta-label) {
    width: 40px;
  }
}

/* Played on the clock, asked from the row: a pink button you press. */
.played-cta {
  flex-shrink: 0;
  height: 40px;
  padding: 0 var(--spacing-l);
  border: 0;
  border-radius: 20px;
  color: var(--color-white);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  @include raised-action;
  @include press;
}

/* A small mark, with a finger-sized target around it that takes no room in the row. */
.close {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-white-50);
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: -12px;
  }

  @include hover {
    color: var(--color-white);
  }
}

.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  align-self: center;
  padding: 2px var(--spacing-md);
  border: 0;
  background: none;
  font-size: 12px;
  color: var(--color-white-50);
  cursor: pointer;

  @include hover {
    color: var(--color-white);
  }
}

.live-banner-enter-active,
.live-banner-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.live-banner-enter-from,
.live-banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
